import { useCallback, useRef } from "react";
import type { EpisodeSummary } from "@/api";
import { useWindowHotkeys } from "@/components/keyboard";
import type { EpisodeRefreshOptions } from "../useEpisodePage";

/**
 * 에피소드 전환 가드. `false` 를 돌려주면 브라우저는 전환을 멈춘다. 가드는 직접 확인 UI 를
 * 띄운다. 사용자가 승낙하면 가드가 전달받은 `apply` 를 호출해 그 전환을 이어서 실행한다.
 */
export type EpisodeChangeGuard = (apply: () => void) => boolean;

// 저장 뒤 이동 결과. `end` 는 저장한 행 다음에 조건에 맞는 에피소드가 없다는 뜻이다. `failed` 는
// 다음 행을 찾으려고 목록을 다시 받았지만 받지 못했다는 뜻이다. 남은 에피소드가 없다는 뜻이 아니다.
export type EpisodeAfterSave = "moved" | "end" | "failed";

// 하단 슬롯(리뷰 독)이 "저장·검토 완료"로 리뷰 루프를 이어 갈 때 쓴다. 앞뒤 이동은 키보드(↑↓)가
// 맡고, 별도 버튼은 두지 않는다.
export type EpisodeNav = {
  /**
   * 저장이 성공한 뒤 부른다. `destination` 이 있으면 그 전환(이탈 확인이 보류한 이동)을 마친다.
   * 없으면 저장 전 목록 순서에서 저장한 행 다음 에피소드를 고른다.
   *
   * 저장한 행이 필터 조건에서 빠져도 다음 행을 건너뛰지 않는다. 저장 전 순서를 기준으로
   * 삼기 때문이다. 다음 행이 아직 받지 않은 페이지에 있으면 목록을 한 페이지 더 받아서 고른다.
   * 저장 전에 보이던 행은 다시 고르지 않는다.
   */
  afterSave: (savedUuid: string, destination?: () => void) => Promise<EpisodeAfterSave>;
  // 전환 직전에 확인할 가드를 등록한다(해제는 null). 이 seam 으로 하단 슬롯은 저장하지 않은
  // 편집이 있을 때 다른 에피소드로 이동하는 것을 막는다.
  setChangeGuard: (guard: EpisodeChangeGuard | null) => void;
};

/**
 * 보이는 순서 기준 앞뒤 이동과 그 키보드 바인딩(↑/↓), 저장 뒤 다음 행 고르기.
 *
 * 핸들러는 렌더 시점 값을 ref 로 읽는다. `afterSave` 는 저장 응답 반영 **직후**, 다시 렌더하기 전에
 * 호출된다. 그 시점의 목록은 아직 저장 전 순서다. 방금 제외한 행이 사라지기 전 순서를 따라야 다음
 * 행을 건너뛰지 않는다.
 *
 * 이 hook 은 미리보기가 아니라 **좌측 목록**에 속한다. 그래서 `preview/` 밖에 둔다.
 */
export function useEpisodeNav({
  items,
  selectedEpisodeUuid,
  hasMore,
  setChangeGuard,
  onSelect,
  onLoadMore,
  refresh,
  project,
  rootRef,
}: {
  items: readonly EpisodeSummary[];
  selectedEpisodeUuid: string | null;
  hasMore: boolean;
  setChangeGuard: (guard: EpisodeChangeGuard | null) => void;
  onSelect: (episode: EpisodeSummary) => void;
  onLoadMore: () => void;
  // 불러온 범위를 제자리에서 다시 받는다(`useEpisodePage.refresh`).
  refresh: (options?: EpisodeRefreshOptions) => Promise<readonly EpisodeSummary[] | null>;
  // 서버 행을 목록에 보이는 행으로 바꾼다(override → 숨김 필터). `items` 와 같은 규칙이어야 한다.
  project: (rows: readonly EpisodeSummary[]) => readonly EpisodeSummary[];
  rootRef: React.RefObject<HTMLDivElement | null>;
}): EpisodeNav {
  const stateRef = useRef({
    items,
    selectedEpisodeUuid,
    hasMore,
    onSelect,
    onLoadMore,
    refresh,
    project,
  });
  stateRef.current = {
    items,
    selectedEpisodeUuid,
    hasMore,
    onSelect,
    onLoadMore,
    refresh,
    project,
  };

  const move = useCallback((direction: 1 | -1) => {
    const current = stateRef.current;
    if (current.items.length === 0) return;
    const index = current.items.findIndex(
      (episode) => episode.uuid === current.selectedEpisodeUuid,
    );
    const base = index >= 0 ? index : 0;
    const nextIndex = Math.max(0, Math.min(current.items.length - 1, base + direction));
    // 아래로 이동해 마지막 구간에 들어가면 다음 페이지를 미리 조회한다(스크롤과 같은 기준).
    if (direction === 1 && current.hasMore && nextIndex >= Math.floor(current.items.length * 0.8)) {
      current.onLoadMore();
    }
    const next = current.items[nextIndex];
    if (next !== undefined) current.onSelect(next);
  }, []);

  const afterSave = useCallback(
    async (savedUuid: string, destination?: () => void): Promise<EpisodeAfterSave> => {
      if (destination !== undefined) {
        destination();
        return "moved";
      }
      // 저장 전 순서다. 이 줄은 반드시 await 앞에 있어야 한다.
      const before = stateRef.current;
      const index = before.items.findIndex((episode) => episode.uuid === savedUuid);
      if (index < 0) return "end";
      const next = before.items[index + 1];
      if (next !== undefined) {
        // 키보드 이동과 같은 기준으로 다음 페이지를 미리 받는다.
        if (before.hasMore && index + 1 >= Math.floor(before.items.length * 0.8)) {
          before.onLoadMore();
        }
        before.onSelect(next);
        return "moved";
      }
      if (!before.hasMore) return "end";
      const seen = new Set(before.items.slice(0, index + 1).map((episode) => episode.uuid));
      const fresh = await before.refresh({ extend: true });
      if (fresh === null) return "failed";
      const after = stateRef.current;
      const candidate = after.project(fresh).find((episode) => !seen.has(episode.uuid));
      if (candidate === undefined) return "end";
      after.onSelect(candidate);
      return "moved";
    },
    [],
  );

  // 에피소드가 선택되어 있지 않으면(파일 미리보기 중) 이동을 끈다. `onPress` 가 없으면 비활성이다.
  // 켜 두면 화살표 한 번에 읽던 파일이 에피소드 영상으로 바뀐다. 기준 없는 이동은 index 0 을
  // 선택하기 때문이다.
  const canMove = selectedEpisodeUuid !== null;

  // 단축키는 이 hook 이 소유한다. 이 컴포넌트는 창 안뿐 아니라 데이터셋 상세 **라우트
  // 페이지**에서도 마운트된다(창이 없는 경로). 창 셸의 ↑/↓ 콜백에 등록하면 그 페이지에서는
  // 이동이 모두 동작하지 않는다. 리스너·가드는 공용 컨트롤러가 소유한다.
  useWindowHotkeys({
    surfaceRef: rootRef,
    bindings: [
      { key: "ArrowUp", onPress: canMove ? () => move(-1) : undefined },
      { key: "ArrowDown", onPress: canMove ? () => move(1) : undefined },
    ],
  });

  return { afterSave, setChangeGuard };
}
